import {
    storedPerDisplayUnit,
    type MeasurementSystem,
    type SizeQuantity,
} from '@archipro-website/localisation';
import type { RangeFilterKeys } from '../type/api';

/** Prices are held in cents and shown in whole currency units in every market. */
const PRICE_RANGE_DIVISOR = 100;

export type SizeRangeFilterKey = Exclude<RangeFilterKeys, 'priceRange'>;

/** The product size each size range filter measures. */
export const SIZE_RANGE_QUANTITIES: Record<SizeRangeFilterKey, SizeQuantity> = {
    lengthRange: 'length',
    widthRange: 'length',
    heightRange: 'length',
    diameterRange: 'length',
    weightRange: 'weight',
};

/** Inches and pounds are coarse enough that shoppers enter fractions, e.g. 35.5 in. */
export const IMPERIAL_SIZE_DECIMAL_PLACES = 2;

export const isSizeRangeFilterKey = (
    filterKey: string,
): filterKey is SizeRangeFilterKey => filterKey in SIZE_RANGE_QUANTITIES;

/**
 * Stored units per display unit for each range filter.
 *
 * Range filter states and Search Service queries hold stored values (cents,
 * micrometres, grams). The URL and the filter inputs carry display values in
 * the browsing market's units: mm and kg on metric markets, inches and pounds
 * on imperial ones. Every URL ⇄ stored conversion for a request must use the
 * divisors of that request's market.
 */
export const getRangeDivisors = (
    measurementSystem: MeasurementSystem,
): Record<RangeFilterKeys, number> => ({
    priceRange: PRICE_RANGE_DIVISOR,
    lengthRange: storedPerDisplayUnit('length', measurementSystem),
    widthRange: storedPerDisplayUnit('length', measurementSystem),
    heightRange: storedPerDisplayUnit('length', measurementSystem),
    diameterRange: storedPerDisplayUnit('length', measurementSystem),
    weightRange: storedPerDisplayUnit('weight', measurementSystem),
});

/** Divisor for one range filter key, or undefined when the key is not a range filter. */
export const getRangeDivisor = (
    filterKey: string,
    measurementSystem: MeasurementSystem,
): number | undefined =>
    getRangeDivisors(measurementSystem)[filterKey as RangeFilterKeys];

/**
 * Decimals a display value keeps. Power-of-ten divisors (cents, mm, kg)
 * convert stored integers exactly at three decimals. Imperial divisors keep
 * two, the precision their inputs accept: a value typed as 1 lb is stored as
 * 454 g, and two decimals bring it back as 1 rather than 1.001.
 */
const getDisplayDecimals = (divisor: number): number =>
    /^10*$/.test(String(divisor)) ? 3 : 2;

/** Stored range value to the display value shown and written to the URL. */
export const rangeStoredToDisplay = (
    stored: number,
    divisor: number,
): number => {
    const factor = 10 ** getDisplayDecimals(divisor);
    return Math.round((stored / divisor) * factor) / factor;
};
